import React from 'react';
import { Smartphone, Users, Brain, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';

export const Services = () => {
  const services = [
    {
      icon: Smartphone,
      title: 'Web & Mobile Development',
      description: 'Custom-built applications that scale with your ambitions. From responsive web platforms to native mobile apps, we engineer solutions that captivate users and drive business growth.',
      features: ['React & React Native', 'Cloud-Native Architecture', 'Progressive Web Apps', 'API Development'],
      color: 'cyan'
    },
    {
      icon: Users,
      title: 'IT Staff Augmentation',
      description: 'Access world-class tech talent on demand. Scale your team instantly with senior developers, architects, and specialists who integrate seamlessly into your workflow.',
      features: ['Senior Engineers', 'Flexible Engagement', 'Rapid Onboarding', 'Cultural Fit Guaranteed'],
      color: 'blue'
    },
    {
      icon: Brain,
      title: 'AI Consultancy',
      description: 'Harness the power of artificial intelligence to revolutionize your operations. From machine learning models to intelligent automation, we turn AI potential into business advantage.',
      features: ['Custom AI Models', 'Automation Strategy', 'Data Analytics', 'LLM Integration'],
      color: 'purple'
    }
  ];

  const scrollToContact = () => {
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section id="services" className="py-24 px-6 relative">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 rounded-full glass mb-4">
            <span className="text-sm text-cyan-400 font-medium">Our Expertise</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-bold mb-6">
            Services That <span className="gradient-text">Drive Results</span>
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            End-to-end technology solutions designed for the modern enterprise
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            const colorClasses = {
              cyan: 'from-cyan-500/20 to-cyan-600/5 border-cyan-500/30 group-hover:border-cyan-400/60',
              blue: 'from-blue-500/20 to-blue-600/5 border-blue-500/30 group-hover:border-blue-400/60',
              purple: 'from-purple-500/20 to-purple-600/5 border-purple-500/30 group-hover:border-purple-400/60'
            };
            
            const iconColorClasses = {
              cyan: 'text-cyan-400 bg-cyan-500/10',
              blue: 'text-blue-400 bg-blue-500/10',
              purple: 'text-purple-400 bg-purple-500/10'
            };

            return (
              <div
                key={index}
                className={`group relative bg-gradient-to-br ${colorClasses[service.color]} backdrop-blur-xl border rounded-2xl p-8 hover:scale-105 transition-all duration-300`}
              >
                <div className={`w-16 h-16 rounded-2xl ${iconColorClasses[service.color]} flex items-center justify-center mb-6`}>
                  <Icon className="w-8 h-8" />
                </div>
                
                <h3 className="text-2xl font-bold mb-4 text-white">{service.title}</h3>
                <p className="text-slate-300 mb-6 leading-relaxed">{service.description}</p>
                
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-slate-400 text-sm">
                      <ArrowRight className="w-4 h-4 mr-2 text-cyan-400" />
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Button 
                  variant="ghost" 
                  className="text-cyan-400 hover:text-cyan-300 hover:bg-cyan-500/10 p-0 h-auto font-semibold"
                  onClick={scrollToContact}
                >
                  Get Started
                  <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};